import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';

@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  // Default timeout duration: 5 minutes (600000 ms)
  private readonly DEFAULT_TIMEOUT = 600000;
  
  // Timeout configurations for specific APIs
  private readonly API_TIMEOUTS: { [key: string]: number } = {
    'api/WebPageCapture': 600000, // 5 minutes
    'api/ClaimMonitor/webPageCapturePDF': 300000, // 5 minutes
    'api/Utility/CaptureAddressScreenshots': 300000, // 5 minutes
  };

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this API has a specific timeout configuration
    const apiPath = this.getApiPath(req.url);
    
    // Skip timeout for APIs that already have timeout handling in their service methods
    // (WebPageCapture and webPageCapturePDF are handled in claimsmonitor.service.ts)
    const skipTimeoutApis = ['api/WebPageCapture', 'api/ClaimMonitor/webPageCapturePDF'];
    if (skipTimeoutApis.includes(apiPath)) {
      return next.handle(req);
    }

    const timeoutDuration = this.API_TIMEOUTS[apiPath] || this.DEFAULT_TIMEOUT;

    // Apply timeout only to POST requests for long-running operations
    // You can customize this logic based on your needs
    const shouldApplyTimeout = req.method === 'POST' && 
      (apiPath in this.API_TIMEOUTS || req.url.includes('Capture') || req.url.includes('CapturePDF'));

    if (shouldApplyTimeout) {
      return next.handle(req).pipe(
        timeout(timeoutDuration),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            console.error(`API timeout for ${apiPath} after ${timeoutDuration}ms:`, error);
            const timeoutError = new HttpErrorResponse({
              error: {
                message: `Request timeout: The operation is taking longer than expected (${Math.round(timeoutDuration / 1000 / 60)} minutes). Please try again.`
              },
              status: 408,
              statusText: 'Request Timeout',
              url: req.url
            });
            return throwError(() => timeoutError);
          }
          
          // Handle 504 Gateway Timeout
          if (error instanceof HttpErrorResponse && error.status === 504) {
            console.error(`Gateway timeout (504) for ${apiPath}:`, error);
            const gatewayTimeoutError = new HttpErrorResponse({
              error: {
                message: 'Gateway timeout: The server is taking too long to respond. Please try again.'
              },
              status: 504,
              statusText: 'Gateway Timeout',
              url: req.url
            });
            return throwError(() => gatewayTimeoutError);
          }
          
          return throwError(() => error);
        })
      );
    }

    // For other requests, proceed without timeout
    return next.handle(req);
  }

  private getApiPath(url: string): string {
    // Extract API path from full URL
    const match = url.match(/api\/[^?]+/);
    return match ? match[0] : '';
  }
}

